import { createProvider } from '@nvision/services/data-provider';
import { queryOptions } from '@tanstack/react-query';

import type { AddressSearchParams } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';
import { api } from './api';
import { normalizeAddressBookSearch, normalizeTimeZones } from './normalize';

export const fetchTimeZones = createProvider(api.fetchTimeZones)
  .andThen((res) => normalizeTimeZones(res))
  .andCatch((error) => {
    if (error instanceof Error && error.name === 'AbortError') {
      logger.warn('Fetch profile aborted');
    } else {
      logger.error('Fetch profile was failed:', error);
    }
    throw error;
  });

export const timeZonesQueryOptions = () =>
  queryOptions({
    queryFn: ({ signal }) => fetchTimeZones({ signal }),
    queryKey: CACHE_KEYS.timeZones(),
    staleTime: 1000 * 60 * 60, // 1 hour
  });

export const fetchAddressBookSearchProvider = createProvider(api.fetchAddressBookSearch)
  .andThen((res) => normalizeAddressBookSearch(res))
  .andCatch((error) => {
    if (error instanceof Error && error.name === 'AbortError') {
      logger.warn('Fetch address book search results list aborted');
    } else {
      logger.error('Fetch address book search results list was failed:', error);
    }
    throw error;
  });

export const addressBookSearchOptions = (
  params: AddressSearchParams,
  options?: { onError?: (error: unknown) => void },
) =>
  queryOptions({
    queryFn: ({ signal }) =>
      fetchAddressBookSearchProvider(params, { signal })
        .then((res) => {
          if (params.offset > 0 && res.data.length === 0) {
            throw new Error('Address book search returned no results for a non-first page');
          }
          return res;
        })
        .catch((error) => {
          options?.onError?.(error);
          throw error;
        }),
    queryKey: CACHE_KEYS.addressBookSearch(params),
  });

export const addAddressProvider = createProvider(api.addAddress)
  .andThen((res) => res)
  .andCatch((error) => {
    if (error instanceof Error && error.name === 'AbortError') {
      logger.warn('Add address aborted');
    } else {
      logger.error('Add address failed:', error);
    }
    throw error;
  });

export const fetchAddressByIdProvider = createProvider(api.fetchAddressById)
  .andThen((res) => res)
  .andCatch((error) => {
    if (error instanceof Error && error.name === 'AbortError') {
      logger.warn('Fetch address by id aborted');
    } else {
      logger.error('Fetch address by id was failed:', error);
    }
    throw error;
  });

export const addressByIdOptions = (id: string) =>
  queryOptions({
    enabled: Boolean(id),
    queryFn: ({ signal }) => fetchAddressByIdProvider(id, { signal }),
    queryKey: CACHE_KEYS.addressById(id),
  });

export const updateAddressProvider = createProvider(api.updateAddress)
  .andThen((res) => res)
  .andCatch((error) => {
    if (error instanceof Error && error.name === 'AbortError') {
      logger.warn('Update address aborted');
    } else {
      logger.error('Update address failed:', error);
    }
    throw error;
  });

export const deleteAddressProvider = createProvider(api.deleteAddress)
  .andThen((res) => res)
  .andCatch((error) => {
    if (error instanceof Error && error.name === 'AbortError') {
      logger.warn('Delete address aborted');
    } else {
      logger.error('Delete address failed:', error);
    }
    throw error;
  });
